import React from 'react';
import PropTypes from 'prop-types';
import {
    getOfficialnessInfo,
    getStudentBMBurstResults,
    getStudentPMResults,
} from '../../../common/utils/studentsListHelpers';
import {
    BURST_MEASURES,
    BURST_MEASURE_GENUS,
    DIBELS8_MEASURES,
    MEASURE_NAMES,
    BASIC_D8_MEASURES,
    BASIC_IDEL2_MEASURES,
    IDEL2_MEASURES,
} from '../../../constants/assessments';
import { resultType, studentType } from '../../../constants/prop-types';
import TableHeader from '../table-header/TableHeader';
import D8PMResultRow from './D8PMResultRow';
import Idel2PMResultRow from './Idel2PMResultRow';
import TRCPMResultRow from './TRCPMResultRow';
import BurstPMResultRow from './BurstPMResultRow';
import { usePopupContext } from '../../../common/contexts/PopupContext';
import { DICTIONARY_KEYS, localize } from '../../../common/localization';

const DATE_SORTING_FIELD = 'clientModDate';
const CREATE_DATE = 'createDate';
const COLUMNS = ['Skill', 'Form', 'Date', 'Score'];

const sortResultsByDate = results =>
    results.sort(
        (a, b) =>
            new Date(b?.[DATE_SORTING_FIELD] ?? b[CREATE_DATE]) -
            new Date(a?.[DATE_SORTING_FIELD] ?? a[CREATE_DATE]),
    );

const getTRCPMResultRow = (result, student, staffId, showOfficialnessPopup) => {
    let displayOfficialnessPopup = null;

    if (staffId !== result.staffUid) {
        const officialnessInfo = getOfficialnessInfo(student, {
            date: result.clientModDate,
            staffName: result.staffName,
        });
        displayOfficialnessPopup = () => showOfficialnessPopup(officialnessInfo);
    }

    return (
        <TRCPMResultRow
            result={result}
            key={`${result.results[0].id}-${MEASURE_NAMES.TRC}-row`}
            displayOfficialnessPopup={displayOfficialnessPopup}
        />
    );
};

const PMHistory = ({ results, student, classId, staffId }) => {
    const pmResults = sortResultsByDate([
        ...getStudentPMResults(results),
        ...getStudentBMBurstResults(results),
    ]);

    const {
        actions: { showOfficialnessPopup },
    } = usePopupContext();

    const getPMResultRow = (result, listOfMeasures, RowComponent, isBurst) => {
        const resultMeasureName = result.measureScores
            ? Object.keys(result.measureScores)
            : [result.measure];
        const initRefineData = isBurst
            ? result.results.filter(probeResult => BURST_MEASURES.includes(probeResult.measure))
            : resultMeasureName;

        return initRefineData
            .sort((a, b) => listOfMeasures.indexOf(a) - listOfMeasures.indexOf(b))
            .map(measureItem => {
                const initMeasureData = isBurst ? measureItem.measure : measureItem;

                return (
                    <RowComponent
                        key={`${result.clientId ?? result.id}-${initMeasureData}-row`}
                        result={result}
                        currentMeasure={initMeasureData}
                        classId={classId}
                        student={student}
                    />
                );
            });
    };

    const renderResultHistory = result => {
        if (result.measure === MEASURE_NAMES.TRC) {
            return getTRCPMResultRow(result, student, staffId, showOfficialnessPopup);
        }
        if (BURST_MEASURE_GENUS.includes(result.measureGenus)) {
            return getPMResultRow(result, BURST_MEASURES, BurstPMResultRow, true);
        }
        if (BASIC_D8_MEASURES.includes(result.measure)) {
            return getPMResultRow(result, DIBELS8_MEASURES, D8PMResultRow, false);
        }
        if (BASIC_IDEL2_MEASURES.includes(result.measure)) {
            return getPMResultRow(result, IDEL2_MEASURES, Idel2PMResultRow, false);
        }
        return null;
    };

    return (
        <div className='dashboard-pm-history'>
            <div className='dashboard-pm-history-header'>
                <div className='dashboard-main-header'>{localize('Progress Monitoring History')}</div>
                <TableHeader columns={COLUMNS} />
            </div>
            {pmResults.length === 0 ? (
                <div className='dashboard-message'>
                    {localize(DICTIONARY_KEYS.NO_PROGRESS_MONITORING_HISTORY_YET)}.
                </div>
            ) : (
                <div className='dashboard-measure-scores'>
                    {pmResults.map(result => {
                        return renderResultHistory(result);
                    })}
                </div>
            )}
        </div>
    );
};

PMHistory.propTypes = {
    student: studentType.isRequired,
    classId: PropTypes.string.isRequired,
    results: PropTypes.shape({
        dibels8: PropTypes.shape({
            BOY: PropTypes.arrayOf(resultType),
            MOY: PropTypes.arrayOf(resultType),
            EOY: PropTypes.arrayOf(resultType),
            PM: PropTypes.arrayOf(resultType),
        }),
    }).isRequired,
    staffId: PropTypes.string.isRequired,
};

export default PMHistory;
